import React, { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { connect, useDispatch } from 'react-redux';

import { BaseMultipleSelectorWithPrompt } from "components/base/BaseMultipleSelectorV2";
import { errorMessage, hasError } from "components/base/ErrorFieldMessage";
import { PermissionCodes } from "config/PermissionCodes";
import { CampaignErrorHandleField } from "./CreateCampaignHandleError";
import ReactHookFormTitleWithRadio from "components/reactHookFormComponents/ReactHookFormTitleWithRadio";
import { DISPLAY_TARGET_CUSTOMER_TYPE, SEGMENT_TYPE, TARGET_CUSTOMER_TYPE } from "config/CustomEnums";
import { createAction } from "utils";

const mapPropsToState = (state) => ({
  segments: state.segments.notPagedAllList,
  levels: state.levels.levelList,
});
const ReactFormTargetCustomerSelector = ({
  className = "",
  selectorTypeFieldName,
  dynamicSegmentFieldName = "dynamicSegments",
  staticSegmentFieldName = "staticSegments",
  selectedLevelsFiledName = "levels",
  segments,
  levels,
  disabled=false,
}) => {
  const dispatch = useDispatch();
  const { watch, setValue, formState: { errors } } = useFormContext();
  const selectedType = watch(selectorTypeFieldName);
  const selectedDynamicSegments = watch(dynamicSegmentFieldName);
  const selectedStaticSegments = watch(staticSegmentFieldName);
  const watchEarningRules = watch('linkedEarningRules');
  const selectedLevels = watch(selectedLevelsFiledName);
  const birthdayStartDate = watchEarningRules?.birthdayTypePeriodStartDate;
  const birthdayEndDate = watchEarningRules?.birthdayTypePeriodEndDate;

  const selectorConfig = {
    [TARGET_CUSTOMER_TYPE.DYNAMIC_SEGMENT]: {
      title: DISPLAY_TARGET_CUSTOMER_TYPE?.[TARGET_CUSTOMER_TYPE.DYNAMIC_SEGMENT],
      namespace: 'segments',
      data: segments,
      selectedList: selectedDynamicSegments,
      setValue: (option) => {
        setValue(dynamicSegmentFieldName, option, { shouldDirty: true })
      },
      addButton: {
        title: 'Add Segments',
      },
      requires: PermissionCodes.addSegment,
      errorIdKey: CampaignErrorHandleField.segment.name,
      defaultFilter: {
        moreSearch: {
          type: SEGMENT_TYPE.DYNAMIC,
          isBlacklist: false,
        },
        ...birthdayStartDate && birthdayEndDate
          ? {
            dateOfBirthRange: [birthdayStartDate, birthdayEndDate],
          }
          : {},
      },
      promptConfig: {
        title: "Go to create segment?",
        description: "Go to create segment",
        rightButton: {
          text: 'Create segments',
          action: () => {
            window.open('/segments/create', '_blank');
          }
        }
      },
      loadSourceDataConfig: {
        action: "segments/getAllList",
        filter: {
          status: true,
          moreSearch: {
            type: SEGMENT_TYPE.DYNAMIC,
            isBlacklist: false,
          },
          ...birthdayStartDate && birthdayEndDate
            ? { dateOfBirthRange: [birthdayStartDate, birthdayEndDate], }
            : {},
        },
      },
    },
    [TARGET_CUSTOMER_TYPE.STATIC_SEGMENT]: {
      title: DISPLAY_TARGET_CUSTOMER_TYPE?.[TARGET_CUSTOMER_TYPE.STATIC_SEGMENT],
      namespace: 'segments',
      data: segments,
      selectedList: selectedStaticSegments,
      setValue: (option) => {
        setValue(staticSegmentFieldName, option, { shouldDirty: true })
      },
      addButton: {
        title: 'Add Segments',
      },
      requires: PermissionCodes.addSegment,
      errorIdKey: CampaignErrorHandleField.segment.name,
      promptConfig: {
        title: "Go to create segment?",
        description: "Go to create segment",
        rightButton: {
          text: 'Create segments',
          action: () => {
            window.open('/segments/create', '_blank');
          }
        }
      },
      loadSourceDataConfig: {
        action: "segments/getAllList",
        filter: {
          status: true,
          moreSearch: {
            type: SEGMENT_TYPE.STATIC,
            isBlacklist: false,
          },
          ...birthdayStartDate && birthdayEndDate
            ? { dateOfBirthRange: [birthdayStartDate, birthdayEndDate], }
            : {},
        },
      },
    },
    [TARGET_CUSTOMER_TYPE.LEVEL]: {
      title: DISPLAY_TARGET_CUSTOMER_TYPE?.[TARGET_CUSTOMER_TYPE.LEVEL],
      namespace: 'levels',
      data: levels,
      selectedList: selectedLevels,
      setValue: (option) => {
        setValue(selectedLevelsFiledName, option, { shouldDirty: true })
      },
      errorIdKey: selectedLevelsFiledName,
      loadSourceDataConfig: {
        action: "levels/getLevelList",
        filter: {},
      },
    },
  }

  const selectorTypeOptions = [
    TARGET_CUSTOMER_TYPE.DYNAMIC_SEGMENT,
    TARGET_CUSTOMER_TYPE.STATIC_SEGMENT,
    TARGET_CUSTOMER_TYPE.LEVEL,
  ]?.map(type => ({
    label: DISPLAY_TARGET_CUSTOMER_TYPE?.[type] || type,
    value: type,
  }))


  const getSelectorWithConfig = (config) => {
    return <BaseMultipleSelectorWithPrompt
      title={config?.title}
      namespace={config?.namespace}
      data={{
        sourceData: config?.data,
        targetData: config?.selectedList,
        targetChange: (value) => {
          config?.setValue && config.setValue(value);
        },
      }}
      addButton={config?.addButton}
      error={{
        error: hasError(errors, config?.errorIdKey),
        id: config?.errorIdKey,
        message: errorMessage(errors, config?.errorIdKey),
      }}
      requires={config?.requires}
      groupKey={config?.groupKey}
      promptConfig={config?.promptConfig}
      loadSourceDataConfig={config?.loadSourceDataConfig}
      disabled={disabled}
    />
  }

  useEffect(() => {
    if (
      [
        TARGET_CUSTOMER_TYPE.DYNAMIC_SEGMENT,
        TARGET_CUSTOMER_TYPE.STATIC_SEGMENT,
      ].includes(selectedType)
    ) {
      const loadSourceDataConfig = selectorConfig?.[selectedType]?.loadSourceDataConfig;
      if (!loadSourceDataConfig?.action) {
        return;
      }
      dispatch(createAction(loadSourceDataConfig?.action)(loadSourceDataConfig?.filter))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType])

  return (<>
    <div
      className={className}
    >
      <ReactHookFormTitleWithRadio
        title={"Select segments"}
        defaultValue={selectedType || TARGET_CUSTOMER_TYPE.DYNAMIC_SEGMENT}
        options={selectorTypeOptions}
        valueFieldName={selectorTypeFieldName}
        disabled={disabled}
      />
    </div>
    <span id={`${CampaignErrorHandleField.segment.name}`} />
    {getSelectorWithConfig(selectorConfig?.[selectedType])}
  </>)
}

export default connect(mapPropsToState)(ReactFormTargetCustomerSelector);
